import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { Route, Switch, useRouteMatch, useParams, useLocation, useHistory } from 'react-router-dom';
import { Pivot, PivotItem, PivotLinkSize } from 'office-ui-fabric-react/lib/Pivot';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { Text } from 'office-ui-fabric-react/lib/Text';
import ContractDetails from '../ContractDetails';
import ContractTransactions from '../ContractTransactions';
import ContractStatements from '../ContractStatements';
import ContractAttachments from '../ContractAttachments';
import { fetchContract } from '../../actions/contracts';
import Link from '../../components/Link';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import ContractAttachmentsActionRequired from '../../components/ContractAttachmentsActionRequired';
import styles from './Contract.style';
import useAPI from '../../hooks/useAPI';
import ContractAdjustments from '../ContractAdjustments';

const Contract = () => {
  const { path, url } = useRouteMatch();
  const { pathname } = useLocation();
  const history = useHistory();
  const [selectedKey, setSelectedKey] = useState('contract');
  const { contractAddress } = useParams();
  const partnerAddress = useSelector(
    state =>
      get(state, `entities.contracts.${contractAddress}.partnerAddress`, null) ||
      get(state, `entities.contracts.${contractAddress}.partner.address`, null),
  );

  const partnerName = useSelector(state =>
    get(state, `entities.contracts.${contractAddress}.partner.name`, null),
  );

  useEffect(() => {
    if (pathname.includes('/transactions')) {
      setSelectedKey('transactions');
    } else if (pathname.includes('/performance')) {
      setSelectedKey('performance');
    } else if (pathname.includes('/statements')) {
      setSelectedKey('statements');
    } else if (pathname.includes('/attachments')) {
      setSelectedKey('attachments');
    } else if (pathname.includes('/adjustments')) {
      setSelectedKey('adjustments');
    } else {
      setSelectedKey('contract');
    }
  }, [pathname]);

  const { isLoading, error } = useAPI(() => fetchContract(contractAddress, { useCache: false }));
  const contract = useSelector(state => get(state, `entities.contracts.${contractAddress}`, null));

  const errorDetail = useMemo(() => {
    if (!error) {
      return null;
    }
    let title = 'Whoops!';
    let message = 'Something went wrong. Please refresh the page to try again.';
    if (error.status === 404) {
      message = 'This contract does not exist. Please try a different link.';
      title = 'Not Found';
    }
    return {
      title,
      message,
    };
  }, [error]);

  if (isLoading) {
    return (
      <Spinner
        size={SpinnerSize.medium}
        className={styles.loadingSpinner}
        data-testid="contractSpinner"
      />
    );
  }

  if (errorDetail) {
    return (
      <>
        <PageTitle>{errorDetail.title}</PageTitle>
        <Main>
          <Text>{errorDetail.message}</Text>
        </Main>
      </>
    );
  }

  return (
    contract && (
      <>
        <PageTitle>
          <Link href={`/partners/${partnerAddress}`}>{partnerName}</Link>
          <span className={styles.slash}> / </span>
          {contract.name}
        </PageTitle>

        <Main>
          {contractAddress && <ContractAttachmentsActionRequired contractId={contractAddress} />}
          <Pivot
            className={styles.tabs}
            aria-label="Contract sections"
            linkSize={PivotLinkSize.large}
            selectedKey={selectedKey}
            onLinkClick={item => {
              history.push(item.props.href);
            }}
          >
            <PivotItem headerText="Contract" itemKey="contract" href={url} />
            <PivotItem
              headerText="Transactions"
              itemKey="transactions"
              href={`${url}/transactions`}
            />
            <PivotItem headerText="Statements" itemKey="statements" href={`${url}/statements`} />

            {window.config.enableContractAttachments && (
              <PivotItem
                headerText="Attachments"
                itemKey="attachments"
                href={`${url}/attachments`}
              />
            )}

            {window.config.enableContractAdjustments && (
              <PivotItem
                headerText="Adjustments"
                itemKey="adjustments"
                href={`${url}/adjustments`}
              />
            )}
            {/* <PivotItem
            headerText="Performance"
            itemKey="performance"
            href={`${url}/performance`}
          /> */}
          </Pivot>

          <Switch>
            <Route exact path={path} component={ContractDetails} />
            <Route exact path={`${path}/transactions`} component={ContractTransactions} />
            <Route exact path={`${path}/statements`} component={ContractStatements} />
            <Route exact path={`${path}/attachments`} component={ContractAttachments} />
            {window.config.enableContractAdjustments && (
              <Route exact path={`${path}/adjustments`} component={ContractAdjustments} />
            )}
          </Switch>
        </Main>
      </>
    )
  );
};

export default Contract;
